import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Phone, Globe, Instagram, Facebook, Linkedin, Clock, Check, X, ArrowUpRight, Download, ChevronLeft, ChevronRight, Lock, Crown, ShieldCheck, Upload, Eye, Star, StarHalf, CornerDownRight } from 'lucide-react';
import { vendorService, authService } from '../services/api';
import { useToast } from '../hooks/useToast';
import authStorage from '../utils/authStorage';
import { isCrossSubdomain, getSubdomainUrl } from '../utils/subdomain';

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'just now';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  
  if (diffMs < 0) {
    return 'just now';
  }
  
  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) {
    return 'just now';
  }
  
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
  }
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }
  
  if (diffDays < 30) {
    const diffWeeks = Math.floor(diffDays / 7);
    return diffWeeks === 1 ? 'a week ago' : `${diffWeeks} weeks ago`;
  }
  
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  }
  
  const diffYears = Math.floor(diffDays / 365);
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
};

const ListingDetails = () => {
  const { id } = useParams();
  const { toast } = useToast();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [pendingClaim, setPendingClaim] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimantName, setClaimantName] = useState('');
  const [claimantPhone, setClaimantPhone] = useState('');
  const [claimantDesignation, setClaimantDesignation] = useState('');
  const [claimComments, setClaimComments] = useState('');
  const [claimProofFile, setClaimProofFile] = useState(null);
  const [claimantNameError, setClaimantNameError] = useState('');
  const [claimantPhoneError, setClaimantPhoneError] = useState('');
  const [claimantDesignationError, setClaimantDesignationError] = useState('');
  const [claimCommentsError, setClaimCommentsError] = useState('');
  const [claimProofFileError, setClaimProofFileError] = useState('');
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [userClaims, setUserClaims] = useState([]);
  const [hasActiveListingOrClaim, setHasActiveListingOrClaim] = useState(false);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [similarListings, setSimilarListings] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ── Session Time Tracking ─────────────────────────────────────────────
  // useRef is used (not useState) so values persist across renders without
  // causing re-renders and are accessible inside event callbacks without stale closures.
  const sessionStartRef = useRef(null);  // Records when the visitor arrived
  const listingIdRef = useRef(null);     // Records which listing is being viewed

  // Reviews States
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [ratingDistribution, setRatingDistribution] = useState({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [userComment, setUserComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});

  const fetchReviews = async (customId) => {
    const targetId = customId || (listing && listing.id);
    if (!targetId) return;
    setReviewsLoading(true);
    try {
      const res = await vendorService.getListingReviews(targetId);
      if (res.success && res.data) {
        setReviews(res.data.reviews || []);
        setAverageRating(res.data.average_rating || 0);
        setTotalReviews(res.data.total_reviews || 0);
        setRatingDistribution(res.data.rating_distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (userRating === 0) {
      toast.error("Please select a rating.");
      return;
    }
    if (!userComment.trim()) {
      toast.error("Please enter a review comment.");
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await vendorService.createReview(listing.id, userRating, userComment);
      if (res.success) {
        toast.success("Review submitted successfully!");
        setUserRating(0);
        setUserComment('');
        setShowReviewModal(false);
        fetchReviews(listing.id);
      } else {
        toast.error(res.error || "Failed to submit review");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error submitting review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const openLightbox = (index) => {
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    if (listing && listing.galleryImageUrls) {
      setActiveImageIndex((prev) => (prev + 1) % listing.galleryImageUrls.length);
    }
  };

  const prevImage = () => {
    if (listing && listing.galleryImageUrls) {
      setActiveImageIndex((prev) => (prev - 1 + listing.galleryImageUrls.length) % listing.galleryImageUrls.length);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') {
        setActiveImageIndex((prev) => (listing?.galleryImageUrls ? (prev + 1) % listing.galleryImageUrls.length : prev));
      }
      if (e.key === 'ArrowLeft') {
        setActiveImageIndex((prev) => (listing?.galleryImageUrls ? (prev - 1 + listing.galleryImageUrls.length) % listing.galleryImageUrls.length : prev));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, listing]);

  // ── Session Time Tracking useEffect ────────────────────────────────
  // Starts a timer when the listing loads and sends the duration to the backend
  // when the visitor leaves the page (tab close, navigation, or SPA route change).
  useEffect(() => {
    if (!listing || !listing.id) return;

    // Start timer when listing finishes loading
    sessionStartRef.current = Date.now();
    listingIdRef.current = listing.id;

    const sendSessionTime = () => {
      const startTime = sessionStartRef.current;
      const lid = listingIdRef.current;

      // Guard: prevents double-sending if multiple exit events fire in sequence
      if (!startTime || !lid) return;

      const durationSeconds = (Date.now() - startTime) / 1000;

      // Only record meaningful sessions: 5 seconds to 30 minutes
      if (durationSeconds >= 5 && durationSeconds <= 1800) {
        vendorService.recordSessionTime(lid, durationSeconds);
      }

      // Reset to prevent double-send
      sessionStartRef.current = null;
      listingIdRef.current = null;
    };

    // Event 1: Tab becomes hidden (user switches tab or minimizes window)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendSessionTime();
      }
    };

    // Event 2: Page is being unloaded (tab close, browser navigation)
    const handlePageHide = () => {
      sendSessionTime();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      // Cleanup: fires when user navigates within the SPA (React component unmounts)
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      sendSessionTime();
    };
  }, [listing?.id]); // Re-runs only when the listing changes, not on every render

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = (err) => reject(err);
      document.head.appendChild(script);
    });
  };

  const toBase64 = async (url) => {
    try {
      const response = await fetch(`${url}?t=${Date.now()}`, { mode: 'cors' });
      if (!response.ok) {
        console.error("fetch response not ok", response.status);
        return '';
      }
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Error fetching image for base64:", err);
      return '';
    }
  };

  const handleDownloadPdf = async () => {
    if (!listing) return;
    setDownloadingPdf(true);
    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

      const { jsPDF } = window.jspdf;

      const logoBase64 = listing.logoUrl ? await toBase64(getImageUrl(listing.logoUrl)) : '';

      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute';
      pdfContainer.style.left = '-9999px';
      pdfContainer.style.top = '0';
      pdfContainer.style.width = '800px';
      pdfContainer.style.backgroundColor = '#FFFFFF';
      pdfContainer.style.color = '#333333';
      pdfContainer.style.fontFamily = 'Inter, system-ui, sans-serif';
      pdfContainer.style.padding = '40px';

      const formatTime = (time) => {
        if (!time) return 'Closed';
        if (time === 'Closed') return 'Closed';

        const match12 = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
        if (match12) {
          const hour = match12[1];
          const min = match12[2];
          const ampm = match12[4].toUpperCase();
          return `${hour.padStart(2, '0')}:${min} ${ampm}`;
        }

        const match24 = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
        if (match24) {
          let hour = parseInt(match24[1], 10);
          const min = match24[2];
          const ampm = hour >= 12 ? 'PM' : 'AM';
          hour = hour % 12;
          if (hour === 0) hour = 12;
          return `${hour.toString().padStart(2, '0')}:${min} ${ampm}`;
        }

        return time;
      };

      let businessHoursText = '';
      if (listing.businessHours) {
        const hb = listing.businessHours;
        const type = hb.type || 'ALWAYS_OPEN';
        const main = hb.mainHours;
        const sat = hb.saturdayHours;
        const sun = hb.sundayHours;
        const custom = hb.customHours || [];

        if (type === 'ALWAYS_OPEN') {
          businessHoursText = 'Every Day: 12:00 AM - 11:59 PM';
        } else if (type === 'MON_TO_SAT') {
          const open = formatTime(main?.openTime || '09:00 AM');
          const close = formatTime(main?.closeTime || '06:00 PM');
          const sunOpen = formatTime(sun?.openTime);
          const sunClose = formatTime(sun?.closeTime);
          const sunText = (sunOpen && sunOpen !== 'Closed' && sunClose && sunClose !== 'Closed') 
            ? `${sunOpen} - ${sunClose}` 
            : 'Closed';
          
          businessHoursText = `Mon - Sat: ${open} - ${close}\nSun: ${sunText}`;
        } else if (type === 'MON_TO_FRI') {
          const open = formatTime(main?.openTime || '09:00 AM');
          const close = formatTime(main?.closeTime || '06:00 PM');
          
          const satOpen = formatTime(sat?.openTime);
          const satClose = formatTime(sat?.closeTime);
          const satText = (satOpen && satOpen !== 'Closed' && satClose && satClose !== 'Closed') 
            ? `${satOpen} - ${satClose}` 
            : 'Closed';

          const sunOpen = formatTime(sun?.openTime);
          const sunClose = formatTime(sun?.closeTime);
          const sunText = (sunOpen && sunOpen !== 'Closed' && sunClose && sunClose !== 'Closed') 
            ? `${sunOpen} - ${sunClose}` 
            : 'Closed';

          businessHoursText = `Mon - Fri: ${open} - ${close}\nSat: ${satText}\nSun: ${sunText}`;
        } else if (type === 'CUSTOM') {
          businessHoursText = custom.map(ch => {
            const open = formatTime(ch.openTime);
            const close = formatTime(ch.closeTime);
            if (ch.isClosed || open === 'Closed' || close === 'Closed' || !open || !close) {
              return `${ch.day}: Closed`;
            }
            return `${ch.day}: ${open} - ${close}`;
          }).join('\n');
        }
      } else {
        businessHoursText = 'Every Day: 12:00 AM - 11:59 PM';
      }

      const isIndividual = listing.listingType === 'INDIVIDUAL';

      pdfContainer.innerHTML = `
        <div style="border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; background: #ffffff; padding-bottom: 24px;">
          <!-- Header Content (Logo & Title) -->
          <div style="padding: 24px 24px 20px 24px; display: flex; align-items: center; gap: 20px;">
            <!-- Logo -->
            <div style="width: 100px; height: 100px; border-radius: 50%; border: 1px solid #e2e8f0; background: #f9fafb; overflow: hidden; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.02);">
              ${logoBase64 ? `<img src="${logoBase64}" style="width: 100%; height: 100%; object-fit: cover;" />` : `<span style="font-size: 32px; color: #2ab391; font-weight: bold;">${listing.companyName.charAt(0)}</span>`}
            </div>
            
            <div style="flex-grow: 1;">
              <div style="margin-bottom: 6px;">
                <span style="color: #2ab391; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; display: inline-block;">
                  ${listing.categoryName || 'Supplier'}
                </span>
              </div>
              <h1 style="font-size: 26px; font-weight: 800; color: #1f2937; margin: 0 0 6px 0; line-height: 1.2;">
                ${listing.companyName}
              </h1>
              <p style="font-size: 13px; color: #6b7280; margin: 0; line-height: 1.4;">
                📍 ${listing.address || 'N/A'}
              </p>
            </div>
          </div>
          
          <!-- Divider -->
          <hr style="border: 0; border-top: 1px solid #f3f4f6; margin: 0 24px;" />
          
          <!-- Grid Content Table -->
          <table style="width: 100%; border-collapse: collapse; margin-top: 24px;">
            <tr>
              <!-- Left Column -->
              <td style="width: 50%; vertical-align: top; padding: 0 24px 0 24px; border-right: 1px solid #f3f4f6;">
                <h3 style="font-size: 15px; font-weight: 700; color: #111827; margin-top: 0; margin-bottom: 0;">
                  Contact Information
                </h3>
                <div style="border-top: 1px solid #2ab391; margin-top: 6px; margin-bottom: 14px;"></div>
                
                <div style="font-size: 12px; color: #4b5563; line-height: 1.8;">
                  ${(isIndividual || listing.membershipPlan !== 'FREE') ? `<p style="margin: 4px 0;"><strong>Phone:</strong> ${listing.phone || 'N/A'}</p>` : ''}
                  <p style="margin: 4px 0;"><strong>Email:</strong> ${listing.email || 'N/A'}</p>
                  ${!isIndividual && listing.website ? `<p style="margin: 4px 0;"><strong>Website:</strong> <span style="color: #2ab391;">${listing.website}</span></p>` : ''}
                  <p style="margin: 4px 0;"><strong>Address:</strong> ${listing.address || 'N/A'}</p>
                </div>
                
                ${!isIndividual ? `
                  <h3 style="font-size: 15px; font-weight: 700; color: #111827; margin-top: 30px; margin-bottom: 0;">
                    Business Details
                  </h3>
                  <div style="border-top: 1px solid #2ab391; margin-top: 6px; margin-bottom: 14px;"></div>
                  
                  <div style="font-size: 12px; color: #4b5563; line-height: 1.8;">
                    <p style="margin: 4px 0;"><strong>Established Since:</strong> ${listing.establishedSince || 'N/A'}</p>
                    <p style="margin: 4px 0;"><strong>Tags:</strong> ${(listing.tags || []).map(t => t.name).join(', ') || 'N/A'}</p>
                    ${listing.brands && listing.brands.length > 0 ? `<p style="margin: 4px 0;"><strong>Brands:</strong> ${listing.brands.map(b => b.name).join(', ')}</p>` : ''}
                  </div>
                ` : ''}

                <h3 style="font-size: 15px; font-weight: 700; color: #111827; margin-top: 30px; margin-bottom: 0;">
                  Business Hours
                </h3>
                <div style="border-top: 1px solid #2ab391; margin-top: 6px; margin-bottom: 14px;"></div>
                
                <div style="font-size: 12px; color: #4b5563; line-height: 1.8; white-space: pre-line;">
                  ${businessHoursText || 'N/A'}
                </div>
              </td>
              
              <!-- Right Column -->
              <td style="width: 50%; vertical-align: top; padding: 0 24px 0 24px;">
                <h3 style="font-size: 15px; font-weight: 700; color: #111827; margin-top: 0; margin-bottom: 0;">
                  About Company
                </h3>
                <div style="border-top: 1px solid #2ab391; margin-top: 6px; margin-bottom: 14px;"></div>
                
                <p style="font-size: 12px; color: #4b5563; line-height: 1.6; margin-top: 0; margin-bottom: 20px; white-space: pre-line; text-align: justify;">
                  ${listing.aboutCompany || 'No description available.'}
                </p>
                
                ${listing.instagram || listing.facebook || listing.linkedin || listing.twitter || (!isIndividual && listing.website) ? `
                  <h3 style="font-size: 15px; font-weight: 700; color: #111827; margin-top: 30px; margin-bottom: 0;">
                    Online Profiles
                  </h3>
                  <div style="border-top: 1px solid #2ab391; margin-top: 6px; margin-bottom: 14px;"></div>
                  
                  <div style="font-size: 12px; color: #4b5563; line-height: 1.8;">
                    ${!isIndividual && listing.website ? `<p style="margin: 4px 0;"><strong>Website:</strong> <span style="color: #2ab391;">${listing.website}</span></p>` : ''}
                    ${listing.instagram ? `<p style="margin: 4px 0;"><strong>Instagram:</strong> ${listing.instagram}</p>` : ''}
                    ${listing.facebook ? `<p style="margin: 4px 0;"><strong>Facebook:</strong> ${listing.facebook}</p>` : ''}
                    ${listing.linkedin ? `<p style="margin: 4px 0;"><strong>LinkedIn:</strong> ${listing.linkedin}</p>` : ''}
                    ${listing.twitter ? `<p style="margin: 4px 0;"><strong>X:</strong> ${listing.twitter}</p>` : ''}
                  </div>
                ` : ''}
              </td>
            </tr>
          </table>
          
          <!-- Footer -->
          <div style="background: #f9fafb; border-top: 1px solid #e5e7eb; padding: 16px; text-align: center; font-size: 11px; color: #9ca3af; border-bottom-left-radius: 16px; border-bottom-right-radius: 16px; margin-top: 36px;">
            Generated from Vendor Top Interiors on ${new Date().toLocaleDateString()}
          </div>
        </div>
      `;

      document.body.appendChild(pdfContainer);

      // Wait a moment for images to render
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await window.html2canvas(pdfContainer, {
        useCORS: true,
        scale: 2
      });

      document.body.removeChild(pdfContainer);

      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `${listing.companyName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_details.pdf`;
      pdf.save(fileName);
      toast.success("PDF catalog downloaded successfully!");
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      toast.error("An error occurred while generating the PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleClaimButtonClick = () => {
    const storedUser = authStorage.getItem('user');
    if (!storedUser) {
      const redirectPath = location.pathname + '?claim=true';
      const targetPath = '/login?redirect=' + encodeURIComponent(redirectPath);
      if (isCrossSubdomain('/login')) {
        window.location.replace(getSubdomainUrl(targetPath));
      } else {
        navigate(targetPath);
      }
    } else {
      if (hasActiveListingOrClaim) {
        toast.warning("You already have an active listing or an approved claim. You cannot claim another listing.");
        return;
      }

      // Check if they already have a pending claim on any listing
      const hasPending = userClaims.some(c => c.status === 'PENDING');
      if (hasPending) {
        toast.warning("You already have a claim request pending approval. You cannot claim another listing until your current request is resolved.");
        return;
      }
      setShowClaimModal(true);
    }
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!listing) return;
    
    let isValid = true;
    
    // Claimant Name Validation
    if (!claimantName.trim()) {
      setClaimantNameError("Full name is required.");
      isValid = false;
    } else if (claimantName.trim().length < 2) {
      setClaimantNameError("Full name must be at least 2 characters.");
      isValid = false;
    } else {
      setClaimantNameError("");
    }

    // Claimant Phone Validation
    if (!claimantPhone.trim()) {
      setClaimantPhoneError("Contact number is required.");
      isValid = false;
    } else if (!/^\d{10}$/.test(claimantPhone.trim())) {
      setClaimantPhoneError("Contact number must be exactly 10 digits.");
      isValid = false;
    } else {
      setClaimantPhoneError("");
    }

    // Claimant Designation Validation
    if (!claimantDesignation.trim()) {
      setClaimantDesignationError("Designation is required.");
      isValid = false;
    } else if (claimantDesignation.trim().length < 2) {
      setClaimantDesignationError("Designation must be at least 2 characters.");
      isValid = false;
    } else {
      setClaimantDesignationError("");
    }

    // Validate file upload: only required if we don't have a previous proof photo URL
    const hasPreviousProof = pendingClaim && pendingClaim.proofPhotoUrl;
    if (!claimProofFile && !hasPreviousProof) {
      setClaimProofFileError("Government proof document is required.");
      isValid = false;
    } else if (claimProofFile) {
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
      const fileExt = claimProofFile.name.split('.').pop().toLowerCase();
      const allowedMimetypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      
      if (!allowedExtensions.includes(fileExt) || !allowedMimetypes.includes(claimProofFile.type)) {
        setClaimProofFileError("Proof document must be a JPG, PNG, or PDF file.");
        isValid = false;
      } else {
        setClaimProofFileError("");
      }
    } else {
      setClaimProofFileError("");
    }

    if (!isValid) {
      toast.error("Please resolve the validation errors before submitting.");
      return;
    }
    
    setSubmittingClaim(true);
    try {
      const formData = new FormData();
      formData.append("claimantName", claimantName);
      formData.append("claimantPhone", claimantPhone);
      formData.append("claimantDesignation", claimantDesignation);
      formData.append("comments", claimComments);
      if (claimProofFile) {
        formData.append("file", claimProofFile);
      }

      const res = await vendorService.claimListing(listing.id, formData);
      if (res.success && res.data) {
        setPendingClaim(res.data);
        const claimsRes = await vendorService.getMyClaims();
        if (claimsRes.success && claimsRes.data) {
          setUserClaims(claimsRes.data);
        }
        setClaimComments('');
        setClaimProofFile(null);
        setClaimCommentsError('');
        setClaimProofFileError('');
        setClaimantDesignation('');
        setClaimantNameError('');
        setClaimantPhoneError('');
        setClaimantDesignationError('');
        setShowClaimModal(false);
        // Clean query parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('claim');
        setSearchParams(newParams, { replace: true });
        toast.success("Claim request submitted successfully! Admin will verify and approve your request.");
      } else {
        toast.error(res.error || "Failed to submit claim request. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred during submission.");
    } finally {
      setSubmittingClaim(false);
    }
  };

  useEffect(() => {
    if (listing && listing.categoryId) {
      const fetchSimilar = async () => {
        try {
          const res = await vendorService.getPublicListings({ categoryId: listing.categoryId }, 0, 4);
          if (res.success && res.data.listings) {
            const filtered = res.data.listings.filter(l => l.id !== listing.id);
            setSimilarListings(filtered);
          }
        } catch (err) {
          console.error("Error loading similar listings:", err);
        }
      };
      fetchSimilar();
    }
  }, [listing]);

  // Inquiry Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    phone: '',
    message: '',
    captchaInput: ''
  });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);
  const [inquiryError, setInquiryError] = useState('');

  const [inquiryTouched, setInquiryTouched] = useState({
    name: false,
    phone: false,
    message: false,
    captchaInput: false
  });

  const validateInquiry = (data) => {
    const errors = {};

    if (!data.name || !data.name.trim()) {
      errors.name = "Name is required.";
    } else if (data.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters.";
    }

    if (!data.phone || !data.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else if (!/^\d{10}$/.test(data.phone.trim())) {
      errors.phone = "Phone number must be exactly 10 digits.";
    }



    if (!data.message || !data.message.trim()) {
      errors.message = "Message details are required.";
    } else if (data.message.trim().length < 10) {
      errors.message = "Message details must be at least 10 characters.";
    }

    if (!data.captchaInput || !data.captchaInput.trim()) {
      errors.captchaInput = "CAPTCHA code is required.";
    }

    return errors;
  };

  const inquiryErrors = validateInquiry(inquiryForm);

  const getInquiryInputClass = (fieldName) => {
    const hasError = inquiryTouched[fieldName] && inquiryErrors[fieldName];
    if (hasError) {
      return "w-full border-2 border-red-400 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 bg-white transition-all text-red-700";
    }
    return "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] bg-white transition-all text-gray-800";
  };

  const handleInquiryBlur = (e) => {
    setInquiryTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState('');

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaError('');
    setInquiryError('');
    try {
      const res = await vendorService.getCaptcha();
      if (res.success && res.data) {
        setCaptchaSvg(res.data.captchaSvg);
        setCaptchaToken(res.data.captchaToken);
        setInquiryForm(prev => ({ ...prev, captchaInput: '' }));
        setInquiryTouched(prev => ({ ...prev, captchaInput: false }));
      } else {
        setCaptchaError(res.error || "Failed to load CAPTCHA. Please click refresh.");
      }
    } catch (err) {
      console.error("Error loading captcha:", err);
      setCaptchaError("Error loading CAPTCHA. Please check your connection.");
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      setInquiryError('');
      loadCaptcha();
      setInquiryTouched({
        name: false,
        phone: false,
        city: false,
        message: false,
        captchaInput: false
      });
      const storedUser = authStorage.getItem('user');
      const token = authStorage.getItem('token');
      if (token && storedUser) {
        try {
          const userObj = JSON.parse(storedUser);
          setInquiryForm(prev => ({
            ...prev,
            name: userObj.name || userObj.username || '',
            phone: '',
            city: '',
            message: '',
            captchaInput: ''
          }));
        } catch (e) {
          console.error(e);
        }
      } else {
        setInquiryForm({ name: '', phone: '', message: '', captchaInput: '' });
      }
    }
  }, [isModalOpen]);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setHasActiveListingOrClaim(false);
      setPendingClaim(null);
      setUserClaims([]);
      try {
        const res = await vendorService.getPublicListingById(id);
        if (res.success && res.data) {
          setListing(res.data);
          
          // Load claim details if user is logged in
          const storedUser = authStorage.getItem('user');
          if (storedUser) {
            const userObj = JSON.parse(storedUser);
            const userEmail = userObj.emailId || userObj.email || '';
            
            // Pre-populate default claimant name and phone from profile
            if (userEmail) {
              const profileRes = await authService.getUserProfile(userEmail);
              if (profileRes.success && profileRes.data) {
                setClaimantName(profileRes.data.name || '');
                setClaimantPhone(profileRes.data.mobileNo || '');
              }
            }

            // Check own listings
            let ownsListing = false;
            const listRes = await vendorService.getMyListings();
            if (listRes.success && listRes.data.listings && listRes.data.listings.length > 0) {
              ownsListing = true;
            }

            const claimsRes = await vendorService.getMyClaims();
            let hasApproved = false;
            if (claimsRes.success && claimsRes.data) {
              setUserClaims(claimsRes.data);
              const matchedClaim = claimsRes.data.find(c => c.listing_id === parseInt(id, 10));
              setPendingClaim(matchedClaim || null);
              hasApproved = claimsRes.data.some(c => c.status === 'APPROVED');
            } else {
              setUserClaims([]);
              setPendingClaim(null);
            }
            setHasActiveListingOrClaim(ownsListing || hasApproved);
          }
          fetchReviews(res.data.id);
        } else {
          setError(res.error || "Failed to load listing details");
        }
      } catch (err) {
        console.error("Error loading listing:", err);
        setError("An error occurred while loading details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return;

    const isClaimQuery = searchParams.get('claim') === 'true';
    const storedUser = authStorage.getItem('user');
    if (isClaimQuery && storedUser && listing) {
      if (hasActiveListingOrClaim) {
        toast.warning("You already have an active listing or an approved claim. You cannot claim another listing.");
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('claim');
        setSearchParams(newParams, { replace: true });
        return;
      }

      // Check if they already have a pending claim on any listing
      const hasPending = userClaims.some(c => c.status === 'PENDING');
      if (hasPending) {
        toast.warning("You already have a claim request pending approval. You cannot claim another listing.");
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('claim');
        setSearchParams(newParams, { replace: true });
        return;
      }
      setShowClaimModal(true);
      // Clean query params so it doesn't auto-open again on page refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('claim');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, listing, userClaims, hasActiveListingOrClaim, loading]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'phone') {
      value = value.replace(/\D/g, '');
    }
    setInquiryForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (inquiryError) {
      setInquiryError('');
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setInquiryError('');

    setInquiryTouched({
      name: true,
      phone: true,
      message: true,
      captchaInput: true
    });

    const errors = validateInquiry(inquiryForm);
    if (Object.keys(errors).length > 0) {
      setInquiryError("Please correct the errors in the form.");
      return;
    }

    setSubmittingInquiry(true);
    try {
      const payload = {
        ...inquiryForm,
        captchaToken: captchaToken
      };
      const res = await vendorService.createInquiry(listing.id, payload);
      if (res.success) {
        setInquirySuccess(true);
        setInquiryForm({ name: '', phone: '', message: '', captchaInput: '' });
        setInquiryTouched({
          name: false,
          phone: false,
          message: false,
          captchaInput: false
        });
        setTimeout(() => {
          setIsModalOpen(false);
          setInquirySuccess(false);
        }, 2500);
      } else {
        setInquiryError(res.error || "Failed to submit inquiry.");
        loadCaptcha(); // Reload captcha for next try
      }
    } catch (err) {
      console.error(err);
      setInquiryError("An error occurred during submission. Please try again.");
      loadCaptcha();
    } finally {
      setSubmittingInquiry(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40 bg-[#f8f9fa] min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2ab391]"></div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="bg-[#f8f9fa] min-h-screen pt-32 pb-16 flex flex-col items-center justify-center">
        <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center shadow-sm max-w-md mx-4">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <X size={28} />
          </div>
          <h3 className="font-bold text-gray-800 text-lg mb-1">Listing Not Found</h3>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            {error || "The listing you are looking for does not exist or has been removed."}
          </p>
          <Link to="/listing" className="bg-[#2ab391] hover:bg-[#23977a] text-white font-medium text-sm px-6 py-2.5 rounded-lg shadow-sm transition-all inline-block">
            Back to Directory
          </Link>
        </div>
      </div>
    );
  }

  // Parse business hours from JSON
  let hoursType = 'ALWAYS_OPEN';
  let mainHours = null;
  let satHours = null;
  let sunHours = null;
  let customHours = [];

  if (listing.businessHours) {
    hoursType = listing.businessHours.type || 'ALWAYS_OPEN';
    mainHours = listing.businessHours.mainHours;
    satHours = listing.businessHours.saturdayHours;
    sunHours = listing.businessHours.sundayHours;
    customHours = listing.businessHours.customHours || [];
  }

  const getWeeklySchedule = () => {
    const daysMap = [
      { full: 'Monday', short: 'Mon' },
      { full: 'Tuesday', short: 'Tue' },
      { full: 'Wednesday', short: 'Wed' },
      { full: 'Thursday', short: 'Thu' },
      { full: 'Friday', short: 'Fri' },
      { full: 'Saturday', short: 'Sat' },
      { full: 'Sunday', short: 'Sun' }
    ];

    const formatTimeStr = (time) => {
      if (!time) return 'Closed';
      if (time === 'Closed') return 'Closed';

      const match12h = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
      if (match12h) {
        const hour = match12h[1];
        const min = match12h[2];
        const ampm = match12h[4].toUpperCase();
        return `${hour.padStart(2, '0')}:${min} ${ampm}`;
      }

      const match24h = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (match24h) {
        let hour = parseInt(match24h[1], 10);
        const min = match24h[2];
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12;
        if (hour === 0) hour = 12;
        return `${hour.toString().padStart(2, '0')}:${min} ${ampm}`;
      }

      return time;
    };

    if (hoursType === 'ALWAYS_OPEN') {
      return daysMap.map(d => ({
        day: d.full,
        hours: '12:00 AM - 11:59 PM',
        closed: false
      }));
    }

    if (hoursType === 'MON_TO_SAT') {
      const open = mainHours?.openTime || '09:00 AM';
      const close = mainHours?.closeTime || '06:00 PM';
      return daysMap.map(d => {
        if (d.short === 'Sun') {
          if (sunHours && sunHours.openTime && sunHours.openTime !== 'Closed' && sunHours.closeTime) {
            return { day: d.full, hours: `${formatTimeStr(sunHours.openTime)} - ${formatTimeStr(sunHours.closeTime)}`, closed: false };
          }
          return { day: d.full, hours: 'Closed', closed: true };
        }
        return { day: d.full, hours: `${formatTimeStr(open)} - ${formatTimeStr(close)}`, closed: false };
      });
    }

    if (hoursType === 'MON_TO_FRI') {
      const open = mainHours?.openTime || '09:00 AM';
      const close = mainHours?.closeTime || '06:00 PM';
      return daysMap.map(d => {
        if (d.short === 'Sun') {
          if (sunHours && sunHours.openTime && sunHours.openTime !== 'Closed' && sunHours.closeTime) {
            return { day: d.full, hours: `${formatTimeStr(sunHours.openTime)} - ${formatTimeStr(sunHours.closeTime)}`, closed: false };
          }
          return { day: d.full, hours: 'Closed', closed: true };
        }
        if (d.short === 'Sat') {
          if (satHours && satHours.openTime && satHours.openTime !== 'Closed' && satHours.closeTime) {
            return { day: d.full, hours: `${formatTimeStr(satHours.openTime)} - ${formatTimeStr(satHours.closeTime)}`, closed: false };
          }
          return { day: d.full, hours: 'Closed', closed: true };
        }
        return { day: d.full, hours: `${formatTimeStr(open)} - ${formatTimeStr(close)}`, closed: false };
      });
    }

    if (hoursType === 'CUSTOM') {
      return daysMap.map(d => {
        const found = customHours.find(ch => ch.day.toLowerCase() === d.short.toLowerCase() || ch.day.toLowerCase() === d.full.toLowerCase());
        if (found) {
          if (found.isClosed || found.openTime === 'Closed' || !found.openTime || !found.closeTime) {
            return { day: d.full, hours: 'Closed', closed: true };
          }
          return { day: d.full, hours: `${formatTimeStr(found.openTime)} - ${formatTimeStr(found.closeTime)}`, closed: false };
        }
        return { day: d.full, hours: 'Closed', closed: true };
      });
    }

    return daysMap.map(d => ({ day: d.full, hours: 'Closed', closed: true }));
  };

  const weeklySchedule = getWeeklySchedule();

  const timeStrToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    timeStr = timeStr.trim();
    const match12 = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match12) {
      let hours = parseInt(match12[1], 10);
      const minutes = parseInt(match12[2], 10);
      const ampm = match12[3].toUpperCase();
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    }
    const match24 = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (match24) {
      const hours = parseInt(match24[1], 10);
      const minutes = parseInt(match24[2], 10);
      return hours * 60 + minutes;
    }
    return 0;
  };

  const checkIsOpenNow = () => {
    if (!weeklySchedule || weeklySchedule.length === 0) return false;

    const now = new Date();
    const currentDayIndex = now.getDay();
    const daysMapping = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDayName = daysMapping[currentDayIndex];

    const todaySchedule = weeklySchedule.find(item => item.day.toLowerCase() === currentDayName.toLowerCase());
    if (!todaySchedule || todaySchedule.closed) return false;

    if (todaySchedule.hours === '12:00 AM - 11:59 PM') return true;

    const timeParts = todaySchedule.hours.split('-');
    if (timeParts.length !== 2) return false;

    const openMinutes = timeStrToMinutes(timeParts[0]);
    const closeMinutes = timeStrToMinutes(timeParts[1]);

    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTotalMinutes = currentHours * 60 + currentMinutes;

    if (closeMinutes < openMinutes) {
      return currentTotalMinutes >= openMinutes || currentTotalMinutes <= closeMinutes;
    }

    return currentTotalMinutes >= openMinutes && currentTotalMinutes <= closeMinutes;
  };

  return (
    <div className="bg-[#f8f9fa] min-h-screen pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Details Header Card */}
        <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6 flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
          {/* Trusted Badge — Mobile Position (Top Right Border) */}
          {listing.membershipPlan === 'PREMIUM' && listing.membershipStatus === 'ACTIVE' && (
            <span className="md:hidden absolute top-3.5 right-3.5 bg-[#2ab391] text-white text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full shadow-xs inline-flex items-center gap-1 z-10">
              <ShieldCheck size={12} className="text-white" /> Trusted
            </span>
          )}

          <div className="flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
            {/* Round Logo */}
            <div className="w-24 h-24 bg-[#b0e0e6] rounded-full border-4 border-[#e0f7f8] flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
              {listing.logoUrl ? (
                <img
                  src={getImageUrl(listing.logoUrl)}
                  alt="Company Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-[#2ab391] font-extrabold text-2xl">
                  {listing.companyName.charAt(0)}
                </span>
              )}
            </div>

            {/* Header Text details */}
            <div className="flex flex-col items-center md:items-start w-full">
              <h1 className="font-extrabold text-2xl md:text-3xl text-gray-800 tracking-tight flex flex-wrap items-center justify-center md:justify-start gap-2">
                {listing.companyName}
                {/* Trusted Badge — Desktop Position (Inline next to Name) */}
                {listing.membershipPlan === 'PREMIUM' && listing.membershipStatus === 'ACTIVE' && (
                  <span className="hidden md:inline-flex items-center gap-1 bg-[#2ab391] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-xs">
                    <ShieldCheck size={12} className="text-white" /> Trusted
                  </span>
                )}
              </h1>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-1 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <MapPin size={15} className="text-[#2ab391]" />
                  <span>{listing.area ? `${listing.area}, ` : ''}{listing.city ? `${listing.city}, ` : ''}{listing.state}</span>
                </div>
                <span className="hidden md:inline-block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                {checkIsOpenNow() ? (
                  <span className="bg-[#eaf7f4] text-[#2ab391] text-xs font-bold px-2.5 py-0.5 rounded-full">
                    Open Now
                  </span>
                ) : (
                  <span className="bg-red-50 text-red-500 text-xs font-bold px-2.5 py-0.5 rounded-full">
                    Closed Now
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-4">
                {/* Show contact details if INDIVIDUAL listing OR PROFESSIONAL/PREMIUM with ACTIVE status */}
                {(listing.listingType === 'INDIVIDUAL' || ((listing.membershipPlan === 'PROFESSIONAL' || listing.membershipPlan === 'PREMIUM') && listing.membershipStatus === 'ACTIVE')) ? (
                  <>
                    {listing.phone && (
                      <a
                        href={`tel:${listing.phone}`}
                        className="flex items-center gap-2 bg-[#2ab391] hover:bg-[#23977a] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all shadow-sm"
                      >
                        <Phone size={14} />
                        <span>{listing.phone}</span>
                      </a>
                    )}
                    {listing.phone && (
                      <a
                        href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-semibold px-4 py-2 rounded-xl transition-all"
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="text-[#25D366]">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        <span>WhatsApp</span>
                      </a>
                    )}
                  </>
                ) : null}
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-semibold px-4 py-2 rounded-xl transition-all disabled:opacity-50 cursor-pointer"
                >
                  {downloadingPdf ? (
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-600"></div>
                  ) : (
                    <Download size={14} />
                  )}
                  <span>{downloadingPdf ? 'Generating PDF...' : 'Download PDF'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Social Profiles Grid */}
          <div className="flex gap-2.5 justify-center md:justify-end mt-2">
            {listing.website && (
              <a href={listing.website} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center text-[#2ab391] hover:bg-[#2ab391] hover:text-white transition-all shadow-sm" title="Website">
                <Globe size={16} />
              </a>
            )}
            {listing.instagram && (
              <a href={listing.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-[#E1306C] hover:bg-[#E1306C] hover:text-white transition-all shadow-sm" title="Instagram">
                <Instagram size={16} />
              </a>
            )}
            {listing.facebook && (
              <a href={listing.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-[#1877F2] hover:bg-[#1877F2] hover:text-white transition-all shadow-sm" title="Facebook">
                <Facebook size={16} />
              </a>
            )}
            {listing.linkedin && (
              <a href={listing.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-sky-50 border border-sky-100 flex items-center justify-center text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white transition-all shadow-sm" title="LinkedIn">
                <Linkedin size={16} />
              </a>
            )}
            {listing.twitter && (
              <a href={listing.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-800 hover:bg-slate-800 hover:text-white transition-all shadow-sm" title="X">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            )}
          </div>
        </div>

        {/* Detailed Grid columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (Quick Info, Services, Gallery, Map) */}
          <div className="lg:col-span-2 space-y-6">

            {/* Quick Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 text-lg mb-4 border-b border-gray-150 pb-2">
                Quick Information
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {listing.aboutCompany || "No description provided."}
              </p>
            </div>

            {/* Services (Tags for Company / Category Name for Individual) */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 text-lg mb-4 border-b border-gray-150 pb-2">
                Services Offered
              </h3>
              {(listing.listingType === 'INDIVIDUAL' || (listing.categoryName && (!listing.tags || listing.tags.length === 0))) ? (
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Check size={16} className="text-[#2ab391] flex-shrink-0" />
                  <span>{listing.categoryName || 'Individual Business Service'}</span>
                </div>
              ) : listing.tags && listing.tags.length > 0 ? (
                <>
                  {/* Desktop Grid Layout */}
                  <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {listing.tags.map(tag => (
                      <div key={tag.id} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check size={16} className="text-[#2ab391] flex-shrink-0" />
                        <span className="line-clamp-1">{tag.name}</span>
                      </div>
                    ))}
                  </div>
                  {/* Mobile Flex Layout */}
                  <div className="flex md:hidden flex-wrap gap-2">
                    {listing.tags.map(tag => (
                      <div key={tag.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-xs font-semibold text-gray-600">
                        <Check size={12} className="text-[#2ab391] flex-shrink-0" />
                        <span>{tag.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-gray-400 text-sm">No services listed.</p>
              )}
            </div>

            {/* Brands Offered (Brands) */}
            {listing.brands && listing.brands.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-bold text-gray-800 text-lg mb-4 border-b border-gray-150 pb-2">
                  Brands Offered
                </h3>
                {/* Desktop Grid Layout */}
                <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {listing.brands.map(brand => (
                    <div key={brand.id} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check size={16} className="text-[#2ab391] flex-shrink-0" />
                      <span className="line-clamp-1">{brand.name}</span>
                    </div>
                  ))}
                </div>
                {/* Mobile Flex Layout */}
                <div className="flex md:hidden flex-wrap gap-2">
                  {listing.brands.map(brand => (
                    <div key={brand.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-xs font-semibold text-gray-600">
                      <Check size={12} className="text-[#2ab391] flex-shrink-0" />
                      <span>{brand.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {((listing.membershipPlan === 'PROFESSIONAL' || listing.membershipPlan === 'PREMIUM') && listing.membershipStatus === 'ACTIVE' && listing.galleryImageUrls && listing.galleryImageUrls.length > 0) && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex justify-between items-center mb-4 border-b border-gray-150 pb-2">
                  <h3 className="font-bold text-gray-800 text-lg">
                    Gallery & Photos
                  </h3>
                  {listing.galleryImageUrls && listing.galleryImageUrls.length > 3 && (
                    <button
                      onClick={() => openLightbox(0)}
                      className="text-sm font-semibold text-[#2ab391] hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      See all {listing.galleryImageUrls.length} photos
                    </button>
                  )}
                </div>

                {listing.galleryImageUrls && listing.galleryImageUrls.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3 md:gap-4">
                    {listing.galleryImageUrls.slice(0, 3).map((imgUrl, index) => {
                      const isLastThumbnail = index === 2 && listing.galleryImageUrls.length > 3;
                      return (
                        <div
                          key={index}
                          onClick={() => openLightbox(index)}
                          className="aspect-square rounded-xl bg-gray-50 border border-gray-100 overflow-hidden relative group cursor-pointer"
                        >
                          <img
                            src={getImageUrl(imgUrl)}
                            alt={`Gallery ${index}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          {isLastThumbnail && (
                            <div className="absolute inset-0 bg-black/55 backdrop-blur-[1px] flex flex-col items-center justify-center text-white hover:bg-black/50 transition-colors">
                              <span className="text-2xl font-black">+{listing.galleryImageUrls.length - 2}</span>
                              <span className="text-[10px] font-bold uppercase tracking-wider mt-0.5">See All Photos</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No gallery photos uploaded.</p>
                )}
              </div>
            )}

            {/* Location & Map Preview */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 text-lg mb-4 border-b border-gray-150 pb-2">
                Location
              </h3>

              {/* Google Map Container */}
              <div className="h-64 rounded-xl border border-gray-200 overflow-hidden relative shadow-inner bg-slate-50">
                <iframe
                  title="Google Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    `${listing.address || ''}, ${listing.city || ''}, ${listing.state || ''} ${listing.zipcode || ''}`.trim()
                  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                ></iframe>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-4 border-b border-gray-150 pb-2">
                <h3 className="font-bold text-gray-800 text-lg">
                  Reviews {totalReviews > 0 && <span className="text-sm font-semibold text-gray-400 ml-1">({totalReviews})</span>}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    if (authStorage.getItem('token')) {
                      setShowReviewModal(true);
                    } else {
                      toast.warning("You must be logged in to leave a review.");
                      navigate('/login', { state: { from: location.pathname } });
                    }
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-sm cursor-pointer"
                >
                  Write a Review
                </button>
              </div>

              {reviewsLoading ? (
                <div className="flex justify-center items-center py-10">
                  <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : totalReviews === 0 ? (
                /* ── No Reviews: show nothing except the button above ── */
                null
              ) : (
                <div className="space-y-6">
                  {/* Reviews Stats Header — only when reviews exist */}
                  <div className="flex flex-col md:flex-row gap-6 p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                    {/* Left: Overall score */}
                    <div className="flex flex-col items-center justify-center text-center md:border-r border-gray-200 md:pr-6 md:w-1/3">
                      <div className="text-4xl font-black text-gray-800 mb-1">{averageRating}</div>
                      <div className="mb-1">
                        <div className="flex gap-0.5 text-amber-400">
                          {[1, 2, 3, 4, 5].map((star) => {
                            if (star <= Math.floor(averageRating)) {
                              return <Star key={star} size={16} fill="currentColor" className="text-amber-400" />;
                            } else if (star - 0.5 <= averageRating) {
                              return <StarHalf key={star} size={16} fill="currentColor" className="text-amber-400" />;
                            } else {
                              return <Star key={star} size={16} fill="none" className="text-gray-200" />;
                            }
                          })}
                        </div>
                      </div>
                      <div className="text-xs font-bold text-gray-400">
                        {totalReviews} {totalReviews === 1 ? 'Review' : 'Reviews'}
                      </div>
                    </div>

                    {/* Right: Stars distribution */}
                    <div className="flex-1 space-y-2">
                      {[5, 4, 3, 2, 1].map((stars) => {
                        const count = ratingDistribution[stars] || 0;
                        const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                        return (
                          <div key={stars} className="flex items-center gap-3 text-xs">
                            <span className="w-8 font-bold text-gray-600 flex justify-end">{stars} ★</span>
                            <div className="flex-grow h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#2ab391] rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="w-8 text-gray-400 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Reviews Feed List — only when reviews exist */}
                  <div className="border-t border-gray-100 pt-6 space-y-4">
                    <h4 className="font-bold text-gray-800 text-sm">Review Feed</h4>
                    <div className="space-y-4">
                      {(showAllReviews ? reviews : reviews.slice(0, 3)).map((rev) => (
                        <div key={rev.id} className="p-5 border border-gray-100 rounded-2xl bg-white shadow-xs space-y-3.5">
                          {/* Reviewer Row */}
                          <div className="flex items-center gap-3">
                            {/* Avatar */}
                            {rev.user_photo_url ? (
                              <img
                                src={getImageUrl(rev.user_photo_url)}
                                alt={rev.user_name}
                                className="w-10 h-10 rounded-full object-cover border border-gray-100 flex-shrink-0"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                  const sibling = e.target.parentNode.querySelector('.initials-fallback');
                                  if (sibling) sibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="initials-fallback w-10 h-10 rounded-full bg-teal-50 border border-teal-100 text-[#2ab391] font-bold text-sm flex items-center justify-center flex-shrink-0 select-none"
                              style={{ display: rev.user_photo_url ? 'none' : 'flex' }}
                            >
                              {rev.user_name ? rev.user_name.charAt(0).toUpperCase() : 'A'}
                            </div>
                            
                            {/* Name and review count */}
                            <div>
                              <h5 className="font-bold text-xs md:text-sm text-gray-800 tracking-tight leading-tight">
                                {rev.user_name || "Anonymous"}
                              </h5>
                              <span className="text-[10px] md:text-[11px] text-gray-400 font-semibold mt-0.5 block">
                                {rev.user_reviews_count || 0} {rev.user_reviews_count === 1 ? 'review' : 'reviews'}
                              </span>
                            </div>
                          </div>

                          {/* Stars and Duration */}
                          <div className="flex items-center gap-2">
                            <div className="flex gap-0.5 text-amber-400">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  size={14}
                                  fill={star <= rev.rating ? 'currentColor' : 'none'}
                                  className={star <= rev.rating ? 'text-amber-400' : 'text-gray-200'}
                                />
                              ))}
                            </div>
                            <span className="text-[11px] text-gray-400 font-semibold">
                              {formatRelativeTime(rev.created_at)}
                            </span>
                          </div>

                          {/* Comment text */}
                          <div>
                            <p className="text-gray-600 text-xs md:text-sm leading-relaxed whitespace-pre-line">
                              {(() => {
                                const comment = rev.comment || '';
                                const isExpanded = expandedReviews[rev.id];
                                const shouldTruncate = comment.length > 180;
                                
                                if (shouldTruncate && !isExpanded) {
                                  return (
                                    <>
                                      {comment.substring(0, 180)}...
                                      <button
                                        onClick={() => setExpandedReviews(prev => ({ ...prev, [rev.id]: true }))}
                                        className="text-[#2ab391] hover:text-[#23977a] font-bold ml-1.5 focus:outline-none cursor-pointer inline-flex items-center transition-colors text-xs"
                                      >
                                        More
                                      </button>
                                    </>
                                  );
                                }
                                return comment;
                              })()}
                            </p>
                          </div>

                          {/* Owner reply */}
                          {rev.reply && (
                            <div className="pl-4 border-l-2 border-gray-200 space-y-1 mt-3">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-bold text-gray-800 text-xs md:text-sm">
                                  Response from the owner
                                </span>
                                <span className="text-[11px] text-gray-400 font-semibold">
                                  {formatRelativeTime(rev.updated_at || rev.created_at)}
                                </span>
                              </div>
                              <p className="text-gray-600 text-xs md:text-sm leading-relaxed whitespace-pre-line">
                                {rev.reply}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {reviews.length > 3 && (
                      <div className="flex justify-center pt-2">
                        <button
                          onClick={() => setShowAllReviews(!showAllReviews)}
                          className="text-xs font-bold text-[#2ab391] hover:text-[#23977a] flex items-center gap-1 bg-teal-50/50 hover:bg-teal-50 px-4 py-2 rounded-xl border border-teal-100 transition-all cursor-pointer uppercase tracking-wider"
                        >
                          {showAllReviews ? "Show Less" : `Show More (${reviews.length - 3})`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Claim Listing Banner / Status */}
            {listing.isClaimed === false && !hasActiveListingOrClaim && (
              pendingClaim && pendingClaim.status === 'PENDING' ? (
                <div className="bg-gradient-to-r from-orange-50 to-orange-100/50 border border-orange-200 rounded-2xl p-3.5 sm:p-5 flex flex-row items-center justify-between gap-3 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
                  <div className="flex items-center gap-2.5 sm:gap-3.5 text-left">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 flex-shrink-0">
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm sm:text-base">Claim Pending</h4>
                      <p className="hidden sm:block text-gray-500 text-sm mt-0.5">Your verification request for this business listing is currently under admin review.</p>
                    </div>
                  </div>
                </div>
              ) : pendingClaim && pendingClaim.status === 'REJECTED' ? (
                <div className="bg-gradient-to-r from-red-50 to-red-100/50 border border-red-200 rounded-2xl p-3.5 sm:p-5 flex flex-row items-center justify-between gap-3 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
                  <div className="flex items-center gap-2.5 sm:gap-3.5 text-left flex-1 min-w-0">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-red-100 flex items-center justify-center text-red-650 flex-shrink-0">
                      <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div className="text-left min-w-0">
                      <h4 className="font-bold text-gray-800 text-sm sm:text-base">Claim Rejected</h4>
                      <p className="hidden sm:block text-gray-500 text-sm mt-0.5 text-left truncate">Reason: <span className="italic font-medium text-red-800">"{pendingClaim.reject_reason || 'No specific explanation provided.'}"</span></p>
                    </div>
                  </div>
                  <button
                    onClick={handleClaimButtonClick}
                    className="bg-red-650 hover:bg-red-750 text-white text-xs sm:text-sm font-bold px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl shadow-md transition-all select-none cursor-pointer active:scale-95 flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0"
                  >
                    Update Claim Details <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-[#2ab391]/10 to-[#b0e0e6]/20 border border-[#2ab391]/20 rounded-2xl p-3.5 sm:p-5 flex flex-row items-center justify-between gap-3 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
                  <div className="flex items-center gap-2.5 sm:gap-3.5 text-left">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-[#2ab391]/10 flex items-center justify-center text-[#2ab391] flex-shrink-0">
                      <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm sm:text-base whitespace-nowrap">Own this business?</h4>
                      <p className="hidden sm:block text-[#475569] text-sm mt-0.5">Claim this listing to verify your business, edit details, upload logos, banners, gallery photos, and manage customer inquiries!</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClaimButtonClick}
                    className="bg-[#2ab391] hover:bg-[#23977a] text-white text-xs sm:text-sm font-bold px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl shadow-md shadow-[#2ab391]/10 hover:shadow-[#2ab391]/20 transition-all select-none cursor-pointer active:scale-95 flex items-center gap-1 sm:gap-1.5 whitespace-nowrap shrink-0"
                  >
                    Claim Listing <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              )
            )}

            {/* Similar Suppliers Section (Desktop only) */}
            {similarListings.length > 0 && (
              <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-6">
                <h3 className="font-bold text-gray-800 text-lg mb-4 border-b border-gray-150 pb-2">
                  Similar {listing.categoryName || ''} Suppliers
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {similarListings.slice(0, 2).map((simListing) => {
                    const displayTags = simListing.tags || [];
                    const showTags = displayTags.slice(0, 2);
                    return (
                      <Link 
                        key={simListing.id} 
                        to={`/listing/${slugify(simListing.companyName)}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition-all flex flex-col bg-white hover:no-underline group cursor-pointer"
                      >
                        {/* Banner Image */}
                        <div className="h-32 bg-white relative block overflow-hidden">
                          {simListing.profileBannerUrl ? (
                            <img
                              src={getImageUrl(simListing.profileBannerUrl)}
                              alt={simListing.companyName}
                              className="w-full h-full object-contain bg-white transition-transform duration-300 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-[#b0e0e6] text-[#2ab391] font-bold text-lg">
                              {simListing.companyName.charAt(0)}
                            </div>
                          )}
                        </div>
                        {/* Content */}
                        <div className="p-4 flex flex-col flex-grow">
                          <h4 className="font-bold text-gray-800 text-sm mb-1 line-clamp-1">{simListing.companyName}</h4>
                          <p className="text-gray-500 text-xs flex items-center gap-1 mb-2">
                            <MapPin size={12} className="text-gray-400" />
                            {simListing.area ? `${simListing.area}, ` : ''}{simListing.city ? `${simListing.city}, ` : ''}{simListing.state}
                          </p>

                          {/* Tags */}
                          {showTags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {showTags.map(t => (
                                <span key={t.id} className="bg-gray-50 text-gray-600 text-[10px] px-2 py-0.5 rounded border border-gray-100">
                                  {t.name}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* View Detail Link */}
                          <div className="mt-auto pt-2 border-t border-gray-50 flex justify-between items-center">
                            <span className="text-[11px] font-bold text-[#2ab391]">{simListing.categoryName}</span>
                            <div className="text-xs font-semibold text-gray-700 group-hover:text-[#2ab391] flex items-center gap-1 transition-colors">
                              View Detail <ArrowUpRight size={14} />
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

          </div>

          {/* Right Column (Contact Info, Business Hours, Inquiry CTA) */}
          <div className="space-y-6">

            {/* Contact Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
              <h3 className="font-bold text-gray-800 text-lg mb-4 border-b border-gray-150 pb-2">
                Contact Info
              </h3>

              <div className="space-y-3.5 text-sm">
                {(listing.listingType === 'INDIVIDUAL' || listing.membershipPlan !== 'FREE') && (
                  <div>
                    <span className="block text-xs font-medium text-gray-400 mb-0.5">Phone</span>
                    <span className="font-semibold text-[#2ab391]">{listing.phone || "Not provided"}</span>
                  </div>
                )}
                <div>
                  <span className="block text-xs font-medium text-gray-400 mb-0.5">Email</span>
                  <span className="text-gray-700 break-words">{listing.email || "Not provided"}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-gray-400 mb-0.5">Address</span>
                  <span className="text-gray-700 leading-relaxed">
                    {listing.address || 'Not provided'}
                  </span>
                </div>
              </div>

              {/* Inquiry Now Button */}
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-6 w-full bg-[#2ab391] hover:bg-[#23977a] text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-sm hover:shadow text-center cursor-pointer"
              >
                Inquiry Now
              </button>
            </div>

            {/* Business Hours Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-4 border-b border-gray-150 pb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-gray-800 text-lg">
                    Business Hours
                  </h3>
                </div>
                {checkIsOpenNow() ? (
                  <span className="bg-[#eaf7f4] text-[#2ab391] text-[11px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                    <Clock size={10} />
                    Open Now
                  </span>
                ) : (
                  <span className="bg-red-50 text-red-500 text-[11px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                    <Clock size={10} />
                    Closed Now
                  </span>
                )}
              </div>

              <div className="space-y-2.5 text-sm text-gray-600">
                {weeklySchedule.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{item.day}</span>
                    <span className={item.closed ? "text-red-500 font-semibold text-[13px]" : "text-gray-600 font-medium"}>
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Share Options Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-bold text-gray-700">Share on</span>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => {
                      const shareUrl = window.location.origin + '/listing/' + slugify(listing.companyName);
                      navigator.clipboard.writeText(shareUrl);
                      toast.success('Listing link copied to clipboard! Opening Instagram...');
                      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
                    }}
                    className="w-9 h-9 rounded-full bg-gray-50 border border-gray-150 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-200 transition-all cursor-pointer"
                    title="Share on Instagram"
                  >
                    <Instagram size={16} />
                  </button>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/listing/' + slugify(listing.companyName))}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-50 border border-gray-150 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-200 transition-all"
                    title="Share on Facebook"
                  >
                    <Facebook size={16} />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin + '/listing/' + slugify(listing.companyName))}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-50 border border-gray-150 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-200 transition-all"
                    title="Share on LinkedIn"
                  >
                    <Linkedin size={16} />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.origin + '/listing/' + slugify(listing.companyName))}&text=${encodeURIComponent(`Check out ${listing.companyName} on Top Interiors India!`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-50 border border-gray-150 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-200 transition-all"
                    title="Share on X"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out ${listing.companyName} on Top Interiors India! ${window.location.origin}/listing/${slugify(listing.companyName)}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-50 border border-gray-150 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-200 transition-all"
                    title="Share on WhatsApp"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.253 8.477 3.52 2.262 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.734-1.459L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.97C16.39 2.012 13.916.99 11.3.99c-5.443 0-9.87 4.372-9.873 9.802-.001 1.73.476 3.41 1.378 4.96l-.999 3.65 3.739-.981.012.009zM17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/>
                    </svg>
                  </a>
                  <a
                    href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.origin + '/listing/' + slugify(listing.companyName))}&description=${encodeURIComponent(`Check out ${listing.companyName} on Top Interiors India!`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full bg-gray-50 border border-gray-150 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-200 transition-all"
                    title="Share on Pinterest"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M12.24 0C5.7 0 0 5.28 0 11.82c0 5.16 3.24 9.48 7.8 11.16-.12-.96-.24-2.4.06-3.42.24-1.02 1.62-6.9 1.62-6.9s-.42-.84-.42-2.1c0-1.98 1.14-3.42 2.58-3.42 1.2 0 1.8.9 1.8 1.98 0 1.2-.78 3-1.2 4.68-.36 1.5.72 2.7 2.16 2.7 2.64 0 4.68-2.76 4.68-6.78 0-3.54-2.52-6-6.18-6-4.2 0-6.66 3.18-6.66 6.42 0 1.26.48 2.64 1.08 3.36.12.12.12.24.06.48-.12.42-.36 1.44-.42 1.62-.06.24-.24.3-.48.18-1.62-.78-2.64-3.24-2.64-5.16 0-4.2 3.06-8.1 8.82-8.1 4.62 0 8.22 3.3 8.22 7.74 0 4.62-2.94 8.34-7.02 8.34-1.38 0-2.64-.72-3.12-1.62l-.84 3.24c-.3 1.14-1.14 2.58-1.74 3.48A12.18 12.18 0 0 0 12.24 24c6.6 0 12-5.4 12-12s-5.4-12-12-12z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Similar Suppliers Section (Mobile only) */}
            {similarListings.length > 0 && (
              <div className="block lg:hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mt-6">
                <h3 className="font-bold text-gray-800 text-base mb-3 border-b border-gray-150 pb-2">
                  Similar {listing.categoryName || ''} Suppliers
                </h3>
                <div className="flex flex-col gap-3">
                  {similarListings.slice(0, 2).map((simListing) => {
                    const displayTags = simListing.tags || [];
                    const showTags = displayTags.slice(0, 2);
                    const extraCount = displayTags.length - showTags.length;
                    const hasBanner = Boolean(simListing.profileBannerUrl);

                    return (
                      <div
                        key={simListing.id}
                        onClick={() => {
                          window.scrollTo(0, 0);
                          navigate(`/listing/${slugify(simListing.companyName)}`);
                        }}
                        className="bg-white rounded-2xl overflow-hidden shadow-xs border border-gray-100 p-3 flex gap-3 hover:shadow-md transition-all cursor-pointer"
                      >
                        {/* Left side: Banner Image Thumbnail or Compact Initial Avatar */}
                        {hasBanner ? (
                          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-white border border-gray-100 flex-shrink-0 relative flex items-center justify-center">
                            <img
                              src={getImageUrl(simListing.profileBannerUrl)}
                              alt={simListing.companyName}
                              className="w-full h-full object-contain bg-white"
                            />
                            {/* Trusted Badge flush on top-left border of thumbnail */}
                            {simListing.membershipPlan === 'PREMIUM' && simListing.membershipStatus === 'ACTIVE' && (
                              <span className="absolute top-0 left-0 z-10 bg-[#2ab391] text-white text-[9px] font-bold px-2 py-0.5 rounded-tl-xl rounded-br-md shadow-xs leading-none">
                                Trusted
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-[#b0e0e6]/70 text-[#2ab391] font-bold text-xl flex-shrink-0 relative flex items-center justify-center border border-gray-100">
                            {simListing.companyName.charAt(0)}
                            {/* Trusted Badge flush on top-left border of thumbnail */}
                            {simListing.membershipPlan === 'PREMIUM' && simListing.membershipStatus === 'ACTIVE' && (
                              <span className="absolute top-0 left-0 z-10 bg-[#2ab391] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-tl-xl rounded-br-md shadow-xs leading-none">
                                Trusted
                              </span>
                            )}
                          </div>
                        )}

                        {/* Right side: ALL Details (Title, Location, Tags, Contact Buttons) */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                          <div>
                            {/* Company Name */}
                            <h4 className="font-bold text-gray-900 text-sm leading-snug truncate mb-0.5">
                              {simListing.companyName}
                            </h4>

                            {/* Location */}
                            <div className="flex items-center gap-1 text-gray-500 text-[11px] truncate mb-1.5">
                              <MapPin size={11} className="text-[#2ab391] flex-shrink-0" />
                              <span className="truncate">{simListing.area ? `${simListing.area}, ` : ''}{simListing.city ? `${simListing.city}, ` : ''}{simListing.state}</span>
                            </div>

                            {/* Category Tags (Strictly single line) */}
                            {showTags.length > 0 && (
                              <div className="flex items-center flex-nowrap gap-1 mb-1.5 overflow-hidden max-w-full">
                                {showTags.map(tag => (
                                  <span key={tag.id} className="bg-gray-50 text-gray-600 text-[10px] font-medium px-2 py-0.5 rounded-md border border-gray-100 whitespace-nowrap truncate max-w-[90px]">
                                    {tag.name}
                                  </span>
                                ))}
                                {extraCount > 0 && (
                                  <span className="bg-[#e0f7f8] text-[#2ab391] text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap shrink-0">
                                    +{extraCount} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Contact Action Bar */}
                          <div className="border-t border-gray-100 pt-1.5 flex items-center justify-between gap-1.5 mt-1">
                            {(simListing.membershipPlan === 'PROFESSIONAL' || simListing.membershipPlan === 'PREMIUM') && simListing.membershipStatus === 'ACTIVE' ? (
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                {simListing.phone ? (
                                  <>
                                    <a
                                      href={`tel:${simListing.phone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex-1 min-w-0 flex items-center justify-center gap-1 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-[11px] font-semibold py-1.5 px-2 rounded-lg transition-colors truncate shadow-xs"
                                    >
                                      <Phone size={11} className="flex-shrink-0" />
                                      <span className="truncate">{simListing.phone}</span>
                                    </a>

                                    <a
                                      href={`https://wa.me/91${simListing.phone.replace(/[^0-9]/g, '')}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex items-center justify-center gap-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-[11px] font-semibold py-1.5 px-2 rounded-lg transition-all shadow-xs"
                                    >
                                      <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" className="text-[#25D366]">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                      </svg>
                                      <span>WhatsApp</span>
                                    </a>
                                  </>
                                ) : null}
                              </div>
                            ) : null}

                            <div
                              className="bg-gray-900 hover:bg-gray-800 text-white p-1.5 rounded-lg transition-colors flex items-center justify-center shrink-0 cursor-pointer shadow-xs"
                              title="View Details"
                            >
                              <ArrowUpRight size={14} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>

      </div>

      {/* Inquiry Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] overflow-y-auto bg-black/50 backdrop-blur-sm flex justify-center items-start md:items-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-100 relative my-8 md:my-auto overflow-hidden">

            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>

            {/* Modal Content */}
            <div className="p-5 sm:p-6">
              <h2 className="font-extrabold text-lg sm:text-xl text-gray-800 mb-2 pr-8">
                Send Inquiry to {listing.companyName}
              </h2>
              {/* <p className="text-gray-500 text-xs mb-4 sm:mb-6">
                Submit this inquiry details, and the business owner will get in touch with you shortly. All fields are required.
              </p> */}

              {inquirySuccess ? (
                <div className="py-8 text-center flex flex-col items-center">
                  <div className="w-16 h-16 bg-[#eaf7f4] text-[#2ab391] rounded-full flex items-center justify-center mb-4">
                    <Check size={32} />
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg mb-1">Inquiry Sent Successfully!</h3>
                  <p className="text-gray-500 text-sm">Your booking inquiry details have been registered.</p>
                </div>
              ) : (
                <form onSubmit={handleInquirySubmit} noValidate className="space-y-3 sm:space-y-4">
                  {inquiryError && (
                    <div className="p-3.5 bg-rose-50 border border-rose-100 text-rose-800 rounded-xl text-xs flex items-center justify-between shadow-sm">
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-rose-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span className="font-semibold">{inquiryError}</span>
                      </div>
                      <button type="button" onClick={() => setInquiryError('')} className="text-rose-500 hover:text-rose-700 font-bold ml-2 text-sm leading-none">×</button>
                    </div>
                  )}

                  {/* Name field */}
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={inquiryForm.name}
                      onChange={handleInputChange}
                      onBlur={handleInquiryBlur}
                      placeholder="Enter your name"
                      className={getInquiryInputClass('name')}
                    />
                    {inquiryTouched.name && inquiryErrors.name && (
                      <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {inquiryErrors.name}
                      </p>
                    )}
                  </div>

                  {/* Phone column */}
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      maxLength={10}
                      value={inquiryForm.phone}
                      onChange={handleInputChange}
                      onBlur={handleInquiryBlur}
                      placeholder="Enter contact number"
                      className={getInquiryInputClass('phone')}
                    />
                    {inquiryTouched.phone && inquiryErrors.phone && (
                      <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {inquiryErrors.phone}
                      </p>
                    )}
                  </div>

                  {/* Message details */}
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Message Details <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={inquiryForm.message}
                      onChange={handleInputChange}
                      onBlur={handleInquiryBlur}
                      rows="3"
                      placeholder="Describe what services or products you are inquiring about..."
                      className={getInquiryInputClass('message') + " resize-none"}
                    ></textarea>
                    {inquiryTouched.message && inquiryErrors.message && (
                      <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {inquiryErrors.message}
                      </p>
                    )}
                  </div>

                  {/* CAPTCHA Verification */}
                  <div className="border-t border-gray-100 pt-4 mt-2 space-y-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      CAPTCHA Verification <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3 items-center">
                      <div className="relative flex-shrink-0 flex items-center gap-2">
                        {captchaLoading ? (
                          <div className="w-[150px] h-[50px] bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-400">
                            Loading...
                          </div>
                        ) : captchaError ? (
                          <div className="w-[150px] h-[50px] bg-red-50 border border-red-200 rounded-lg flex items-center justify-center text-[10px] text-red-500 p-1 text-center leading-tight">
                            {captchaError}
                          </div>
                        ) : (
                          <div
                            className="flex-shrink-0"
                            dangerouslySetInnerHTML={{ __html: captchaSvg }}
                          />
                        )}
                        <button
                          type="button"
                          onClick={loadCaptcha}
                          disabled={captchaLoading}
                          className="p-2 border border-gray-200 hover:border-[#2ab391] hover:text-[#2ab391] rounded-lg transition-colors bg-white flex items-center justify-center text-gray-400 disabled:opacity-50 cursor-pointer h-[50px] w-[50px]"
                          title="Refresh CAPTCHA"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${captchaLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M21 3v5h-5" />
                          </svg>
                        </button>
                      </div>

                      <div className="flex-1 w-full">
                        <input
                          type="text"
                          name="captchaInput"
                          value={inquiryForm.captchaInput}
                          onChange={handleInputChange}
                          onBlur={handleInquiryBlur}
                          placeholder="Enter Captcha Code"
                          className={getInquiryInputClass('captchaInput') + " uppercase tracking-widest font-bold"}
                          maxLength={6}
                        />
                      </div>
                    </div>
                    {inquiryTouched.captchaInput && inquiryErrors.captchaInput && (
                      <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {inquiryErrors.captchaInput}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={submittingInquiry}
                      className="w-full bg-[#2ab391] hover:bg-[#23977a] disabled:opacity-50 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-sm cursor-pointer text-center"
                    >
                      {submittingInquiry ? 'Sending Inquiry...' : 'Submit Inquiry'}
                    </button>
                  </div>
                </form>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && listing && listing.galleryImageUrls && (
        <div className="fixed inset-0 z-[150] bg-black/95 backdrop-blur-md flex flex-col justify-between select-none">
          {/* Top Navbar */}
          <div className="flex justify-between items-center px-6 py-4 text-white bg-gradient-to-b from-black/50 to-transparent">
            <span className="text-sm font-semibold tracking-wide">
              {activeImageIndex + 1} / {listing.galleryImageUrls.length}
            </span>
            <button
              onClick={closeLightbox}
              className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            >
              <X size={24} className="text-white" />
            </button>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex items-center justify-between px-4 md:px-12 relative">
            {/* Prev Button */}
            <button
              onClick={prevImage}
              className="absolute left-4 md:left-8 z-[160] p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors cursor-pointer border border-white/10 shadow-lg"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Active Image Container */}
            <div
              className="w-full h-full flex items-center justify-center p-4 cursor-zoom-out"
              onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
            >
              <img
                src={getImageUrl(listing.galleryImageUrls[activeImageIndex])}
                alt={`Gallery Active ${activeImageIndex}`}
                className="w-auto h-auto md:w-full md:h-full max-h-[70vh] md:max-h-[80vh] max-w-full md:max-w-[80vw] rounded-lg object-contain shadow-2xl transition-all duration-300 hover:scale-[1.01]"
              />
            </div>

            {/* Next Button */}
            <button
              onClick={nextImage}
              className="absolute right-4 md:right-8 z-[160] p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors cursor-pointer border border-white/10 shadow-lg"
            >
              <ChevronRight size={28} />
            </button>
          </div>

          {/* Thumbnail Strip */}
          <div className="bg-black/60 py-4 border-t border-white/10 flex justify-center">
            <div className="flex gap-2 overflow-x-auto max-w-[90vw] px-4 py-1 no-scrollbar">
              {listing.galleryImageUrls.map((imgUrl, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIndex(idx)}
                  className={`relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border-2 transition-all ${idx === activeImageIndex ? 'border-[#2ab391] scale-105 shadow-md shadow-[#2ab391]/20' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                >
                  <img
                    src={getImageUrl(imgUrl)}
                    alt={`Thumbnail ${idx}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showClaimModal && listing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[9999] flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-gray-150 overflow-hidden relative flex flex-col my-8 md:my-16 animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              type="button"
              onClick={() => {
                setShowClaimModal(false);
                setClaimantDesignation('');
                setClaimComments('');
                setClaimProofFile(null);
                setClaimantNameError('');
                setClaimantPhoneError('');
                setClaimantDesignationError('');
                setClaimCommentsError('');
                setClaimProofFileError('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-650 hover:bg-gray-100 p-1.5 rounded-full transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex flex-col gap-1 text-left bg-gray-50/50">
              <span className="text-[10px] uppercase font-bold text-[#2ab391] tracking-wider">Verification Request</span>
              <h3 className="text-lg font-bold text-gray-800">Claim Business Ownership</h3>
              <p className="text-xs text-gray-500 font-medium">Please provide accurate verification details to claim <strong>{listing.companyName}</strong>.</p>
            </div>

            {/* Form */}
            <form onSubmit={handleClaimSubmit} className="flex flex-col flex-1 overflow-y-auto max-h-[75vh]">
              <div className="p-6 space-y-4 text-left">
                {/* Claimant Name */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Claimant Full Name<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={claimantName}
                    onChange={(e) => {
                      setClaimantName(e.target.value);
                      if (e.target.value.trim().length >= 2) setClaimantNameError('');
                    }}
                    placeholder="Enter your full name"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-all text-gray-750 ${
                      claimantNameError ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[#2ab391]/20 focus:border-[#2ab391]'
                    }`}
                  />
                  {claimantNameError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimantNameError}
                    </p>
                  )}
                </div>

                {/* Claimant Phone */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Contact Phone Number<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    maxLength={10}
                    value={claimantPhone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setClaimantPhone(val);
                      if (/^\d{10}$/.test(val)) setClaimantPhoneError('');
                    }}
                    placeholder="10-digit mobile number"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-all text-gray-750 ${
                      claimantPhoneError ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[#2ab391]/20 focus:border-[#2ab391]'
                    }`}
                  />
                  {claimantPhoneError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimantPhoneError}
                    </p>
                  )}
                </div>

                {/* Designation */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Designation / Role<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={claimantDesignation}
                    onChange={(e) => {
                      setClaimantDesignation(e.target.value);
                      if (e.target.value.trim().length >= 2) setClaimantDesignationError('');
                    }}
                    placeholder="e.g. Business Owner, Store Manager"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-all text-gray-750 ${
                      claimantDesignationError ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[#2ab391]/20 focus:border-[#2ab391]'
                    }`}
                  />
                  {claimantDesignationError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimantDesignationError}
                    </p>
                  )}
                </div>

                {/* Comments / Description */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Description / Comments <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <textarea
                    rows={3}
                    value={claimComments}
                    onChange={(e) => setClaimComments(e.target.value)}
                    placeholder="Provide additional details or references to verify your connection to this business."
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all text-gray-750 resize-none"
                  />
                </div>

                {/* Government Proof Document */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">
                    Government Photo Proof Document
                    {(!pendingClaim || !pendingClaim.proofPhotoUrl) && <span className="text-red-500">*</span>}
                  </label>
                  
                  {pendingClaim && pendingClaim.proofPhotoUrl && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs text-slate-650 mb-2">
                      <span className="truncate max-w-[250px]">
                        📄 Previous document: <a href={getImageUrl(pendingClaim.proofPhotoUrl)} target="_blank" rel="noreferrer" className="text-[#2ab391] hover:underline font-semibold">View uploaded proof</a>
                      </span>
                      <span className="text-[10px] text-gray-400 font-semibold">(Optional to upload new proof)</span>
                    </div>
                  )}

                  <div className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 relative h-28 ${
                    claimProofFileError ? 'border-red-400 bg-red-50/10' : 'border-gray-300'
                  }`}>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setClaimProofFile(e.target.files[0]);
                          setClaimProofFileError('');
                        }
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                    <Upload className={claimProofFileError ? "text-red-400 mb-1" : "text-[#2ab391] mb-1"} size={20} />
                    <p className={`text-xs font-semibold ${claimProofFileError ? 'text-red-500' : 'text-gray-700'}`}>
                      {claimProofFile ? `Selected: ${claimProofFile.name}` : 'Click or drag to upload proof document'}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">GSTIN, Business License, or electricity bill (Max 5MB)</p>
                  </div>
                  {claimProofFileError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimProofFileError}
                    </p>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-6 bg-slate-50 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowClaimModal(false);
                    setClaimantDesignation('');
                    setClaimComments('');
                    setClaimProofFile(null);
                    setClaimantNameError('');
                    setClaimantPhoneError('');
                    setClaimantDesignationError('');
                    setClaimCommentsError('');
                    setClaimProofFileError('');
                  }}
                  disabled={submittingClaim}
                  className="px-5 py-2.5 border border-gray-300 text-gray-650 hover:text-gray-800 text-sm font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 cursor-pointer active:scale-98"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingClaim}
                  className="px-6 py-2.5 bg-[#2ab391] hover:bg-[#23977a] text-white text-sm font-bold rounded-xl shadow-md shadow-teal-500/10 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-98"
                >
                  {submittingClaim ? 'Submitting...' : 'Submit Claim'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReviewModal && listing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[9999] flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-gray-150 overflow-hidden relative flex flex-col my-8 md:my-16 animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              type="button"
              onClick={() => {
                setShowReviewModal(false);
                setUserRating(0);
                setUserComment('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-650 hover:bg-gray-100 p-1.5 rounded-full transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex flex-col gap-1 text-left bg-gray-50/50">
              <span className="text-[10px] uppercase font-bold text-[#2ab391] tracking-wider">Submit Feedback</span>
              <h3 className="text-lg font-bold text-gray-800">Write a Review</h3>
              <p className="text-xs text-gray-500 font-medium">Share your experience with <strong>{listing.companyName}</strong>.</p>
            </div>

            {/* Form */}
            <form onSubmit={handleReviewSubmit} className="flex flex-col flex-1 overflow-y-auto max-h-[75vh]">
              <div className="p-6 space-y-5 text-left">
                {/* Your Rating select */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Your Rating<span className="text-red-500">*</span></label>
                  <div className="flex gap-1.5 py-1 text-gray-250">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setUserRating(star)}
                        className="cursor-pointer transition-all duration-150 hover:scale-110 active:scale-95 focus:outline-none"
                      >
                        <Star
                          size={28}
                          fill={star <= userRating ? '#fbbf24' : 'none'}
                          className={star <= userRating ? 'text-amber-400' : 'text-gray-300'}
                        />
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400">Select 1 to 5 stars for this business.</p>
                </div>

                {/* Comment Textarea */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Review Comment<span className="text-red-500">*</span></label>
                  <textarea
                    value={userComment}
                    onChange={(e) => setUserComment(e.target.value)}
                    placeholder="Describe your service experience, quality of work, and pricing details..."
                    rows={4}
                    className="w-full border border-gray-200 rounded-xl p-3 text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] placeholder-gray-400"
                    required
                  />
                  <p className="text-[10px] text-gray-400 font-medium">Please provide a descriptive comment for other customers.</p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowReviewModal(false);
                    setUserRating(0);
                    setUserComment('');
                  }}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingReview}
                  className="px-5 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-xl text-xs font-bold active:scale-[0.98] transition-all cursor-pointer shadow-md shadow-[#2ab391]/10 disabled:opacity-50"
                >
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ListingDetails;
